// @flow

import { get } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  type NameSpoofCountsByDate,
  defaultNameSpoofLineGraphData,
  mungeLineGraphDataNameSpoof,
} from './dataTypesAndUtils';

import PaddedContent from 'ui/atoms/PaddedContent';
import BlockHeader from 'ui/molecules/BlockHeader';
import ContentBlock from 'ui/molecules/ContentBlock';
import PlaceholderLineGraph from 'ui/molecules/PlaceholderLineGraph';
import RingChart from 'ui/molecules/RingChart';
import DataView from 'ui/organisms/DataView';
import SectionOverview from 'ui/organisms/SectionOverview';
import Split from 'ui/templates/Split';
import withDataHandler from 'utils/withDataHandler';

type Data = {
  lineGraph: {
    current: NameSpoofCountsByDate[],
  },
  ringChart: number,
  sectionOverview: {
    current: number,
    previous: number,
  },
};

type Props = {
  data: Data,
  error: boolean,
  loading: boolean,
};

const DataViewWithDataHandler = withDataHandler({
  empty: <PlaceholderLineGraph />,
})(DataView);

export default function NameSpoofs({ data, error, loading }: Props) {
  const lineGraphData = get(data, 'lineGraph.current', defaultNameSpoofLineGraphData);
  const previouslineGraphData = get(data, 'lineGraph.previous', defaultNameSpoofLineGraphData);
  const ringChartData = get(data, 'ringChart', 0);
  const sectionOverviewData = get(data, 'sectionOverview', { current: 0, previous: 0 });

  const { t } = useTranslation('email');

  return (
    <ContentBlock
      top={
        <>
          <BlockHeader pullBottom title={t('nameSpoof')} />
          <Split>
            <SectionOverview
              currentValue={sectionOverviewData.current}
              dimmed={loading}
              formatValue="number"
              previousValue={sectionOverviewData.previous}
            />
            <RingChart formatValue="number" label={t('totalSpoof')} percent={ringChartData} />
          </Split>
          <PaddedContent pushTop="xl">
            <DataViewWithDataHandler
              currentLineProps={{ tint: 'aquamarine' }}
              data={{
                emailPhish: {
                  current: mungeLineGraphDataNameSpoof(lineGraphData),
                  previous: mungeLineGraphDataNameSpoof(previouslineGraphData),
                },
              }}
              dataTestId="data-view-name-spoof"
              isEmpty={() => loading || error || lineGraphData.length === 0}
              isLoading={loading}
              previousLineProps={{ tint: 'sulu' }}
            />
          </PaddedContent>
        </>
      }
    />
  );
}
